import { FC } from 'react';
import { matchRoutes, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setAssumedOrganization, setAssumedRole, sidebarCollapsedSelector, toggleSidebarCollapsed } from '@store/slices/portal.slice';
import { useLoggedinUser } from '@hooks';
import { ROUTES } from '@routes';
import { IRoute, PORTAL_URL } from '@models';
import { Tr } from '@utils';
import { Avatar, Button, List, Stack, Tooltip } from '@mui/material';
import { Logo, RouterLinkListItem } from '@components/common';
import { UserInfo } from '../UserInfo';
import { UserActions } from '../UserActions';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import UndoIcon from '@mui/icons-material/Undo';

const SIDEBAR_TOGGLE_ICON_SIZE = 36;

export const Sidebar: FC = () => {
	const loc = useLocation();
	const user = useLoggedinUser();
	const dispatch = useDispatch();
	const sidebarCollapsed = useSelector(sidebarCollapsedSelector);
	const matches = matchRoutes(ROUTES, loc);
	const route = matches?.[0].route as IRoute;
	const { path, children = [] } = route;
	const childRoutes = children.filter((child) => !child?.hideFromMenu);
	const hasAnyChildRouteIcon = childRoutes.some((child) => child?.icon);

	// const [conversionListCompleted, setConversionListCompleted] = useState(false);
	// const { data: carListBands = [] } = useService(() => carListBandService.getAllFromCache());
	// const { data: sites = [] } = useService(() => siteService.getAllFromCache());
	// const { data: vehicles = [] } = useService(() => fleetVehicleService.getAllFromCache());
	// const { data: simulation } = useService(() => simulationService.getCachedSimulation());
	// const { data: tasks } = useService(() => simulationService.getCachedTasks());

	// useEffect(() => {
	// 	const isCompleted =
	// 		!!tasks &&
	// 		tasks
	// 			.filter((task) => task?.status === SimulationTaskStatus.FULFILLED)
	// 			.every((task) => !!task.fleetVehicle?.selectedCarModelId);
	// 	setConversionListCompleted(isCompleted);
	// }, [tasks]);

	return (
		<Stack
			direction="column"
			justifyContent="space-between"
			sx={{
				boxSizing: 'border-box',
				height: 1.0,
				paddingY: 5,
				backgroundColor: 'secondary.main',
				color: 'common.white',
				position: 'relative'
			}}
		>
			<Avatar
				sx={{
					position: 'absolute',
					cursor: 'pointer',
					bottom: 6,
					right: SIDEBAR_TOGGLE_ICON_SIZE / -2,
					width: SIDEBAR_TOGGLE_ICON_SIZE,
					height: SIDEBAR_TOGGLE_ICON_SIZE,
					backgroundColor: 'secondary.main',
					border: (theme) => `4px solid ${theme.palette.background.default}`,
					boxSizing: 'border-box',
					zIndex: 4
				}}
				onClick={() => dispatch(toggleSidebarCollapsed())}
			>
				<KeyboardDoubleArrowLeftIcon
					sx={{
						color: 'common.white',
						cursor: 'pointer',
						transform: `rotate(${sidebarCollapsed ? '180' : '0'}deg)`,
						transition: 'all 0.5s'
					}}
				/>
			</Avatar>
			<Stack direction="column" alignItems="center" spacing={1}>
				<Logo
					variant="white"
					roleIdentifier
					sx={{ paddingX: sidebarCollapsed ? 0 : 5 }}
					svgIconProps={{ sx: { marginX: 'auto' } }}
					size={sidebarCollapsed ? 'small' : 'normal'}
				/>
				{user?.assumedRole ? (
					<Tooltip
						title={sidebarCollapsed ? <Tr.Portal path="common.sidebar-reset-role-button" /> : null}
						arrow
						placement="right"
					>
						<Button
							variant={sidebarCollapsed ? 'outlined' : 'text'}
							size="small"
							sx={{ width: 'fit-content', minWidth: 40, paddingX: sidebarCollapsed ? 0 : undefined }}
							onClick={() => {
								dispatch(setAssumedOrganization(null));
								dispatch(setAssumedRole(null));
								setTimeout(() => (window.location.href = `/${PORTAL_URL}`), 500);
							}}
						>
							<Stack direction="row" alignItems="center" spacing={1}>
								<UndoIcon fontSize="small" />
								{!sidebarCollapsed ? (
									<span>
										<Tr.Portal path="common.sidebar-reset-role-button" />
									</span>
								) : null}
							</Stack>
						</Button>
					</Tooltip>
				) : null}
			</Stack>
			<List sx={{ paddingY: 0, paddingX: sidebarCollapsed ? 3 : 5 }}>
				{childRoutes.map((childRoute, i) => {
					if (!childRoute.canActivate || childRoute.canActivate(childRoute, user)) {
						const navigateTo = `${path ? path + '/' : ''}${childRoute.path}`;
						// let disabledText: ReactNode = null;

						// if (navigateTo.includes(PortalRoutePath.VEHICLE_LIST)) {
						// 	if (!(carListBands.length && sites.length)) {
						// 		disabledText = <Tr.Portal path="disabled-text-for-empty-car-list-band-and-location" />;
						// 	}
						// } else if (navigateTo.includes(PortalRoutePath.CONVERSION_LIST)) {
						// 	if (!(carListBands.length && sites.length)) {
						// 		disabledText = <Tr.Portal path="disabled-text-for-empty-car-list-band-and-location" />;
						// 	} else if (!vehicles.length) {
						// 		disabledText = <Tr.Portal path="disabled-text-for-empty-vehicle-list" />;
						// 	} else if (!simulation) {
						// 		disabledText = <Tr.Portal path="disabled-text-for-no-simulation" />;
						// 	}
						// } else if (navigateTo.includes(PortalRoutePath.WIZARD)) {
						// 	if (!(carListBands.length && sites.length)) {
						// 		disabledText = <Tr.Portal path="disabled-text-for-empty-car-list-band-and-location" />;
						// 	} else if (!vehicles.length) {
						// 		disabledText = <Tr.Portal path="disabled-text-for-empty-vehicle-list" />;
						// 	} else if (!simulation) {
						// 		disabledText = <Tr.Portal path="disabled-text-for-no-simulation" />;
						// 	} else if (!conversionListCompleted) {
						// 		disabledText = <Tr.Portal path="disabled-text-for-not-completed-conversion-list" />;
						// 	}
						// }

						return (
							<RouterLinkListItem
								key={`child-menu-item-${childRoute.path}-${i}`}
								href={navigateTo}
								hasInset={hasAnyChildRouteIcon}
								route={childRoute}
								// disabledText={disabledText}
								dense={sidebarCollapsed}
							/>
						);
					} else {
						return null;
					}
				})}
			</List>
			<Stack direction="column" spacing={2} alignItems="center">
				<UserInfo sidebarCollapsed={sidebarCollapsed} />
				<UserActions sidebarCollapsed={sidebarCollapsed} />
			</Stack>
		</Stack>
	);
};
